// imports
import { Box, Link } from '@mui/material';
import { Icon } from '@iconify/react';
import { USGS_BLUE } from '../../constants/colors';

const NAV_LINKS = [
  { label: 'DOI Privacy Policy', href: 'https://www.doi.gov/privacy' },
  { label: 'Legal', href: 'https://www.usgs.gov/policies-and-notices' },
  { label: 'Accessibility', href: 'https://www.usgs.gov/accessibility' },
  { label: 'Site Map', href: 'https://www.usgs.gov/sitemap' },
  { label: 'Contact USGS', href: 'https://answers.usgs.gov/' },
  { label: 'U.S. Department of the Interior', href: 'https://www.doi.gov/' },
  { label: 'DOI Inspector General', href: 'https://www.doioig.gov/' },
  { label: 'White House', href: 'https://www.whitehouse.gov/' },
  { label: 'No Fear Act', href: 'https://www.doi.gov/pmb/eeo/no-fear-act' },
  { label: 'FOIA', href: 'https://www.usgs.gov/foia' },
];

const SOCIAL_LINKS = [
  { label: 'X', href: 'https://x.com/usgs', ariaLabel: 'X link', icon: 'ri:twitter-x-fill', width: 24, height: 24, containerWidth: 24, containerHeight: 24 },
  { label: 'Facebook', href: 'https://facebook.com/usgeologicalsurvey', ariaLabel: 'facebook link', icon: 'fa-brands:facebook-square', width: 24, height: 24, containerWidth: 21, containerHeight: 24 },
  { label: 'GitHub', href: 'https://github.com/usgs', ariaLabel: 'github link', icon: 'fa-brands:github', width: 22, height: 24, containerWidth: 21, containerHeight: 24 },
  { label: 'Flickr', href: 'https://flickr.com/usgeologicalsurvey', ariaLabel: 'flickr link', icon: 'fa-brands:flickr', width: 24, height: 24, containerWidth: 21, containerHeight: 24 },
  { label: 'YouTube', href: 'http://youtube.com/usgs', ariaLabel: 'youtube link', icon: 'fa-brands:youtube-square', width: 24, height: 24, containerWidth: 21, containerHeight: 24 },
  { label: 'Instagram', href: 'https://instagram.com/usgs', ariaLabel: 'instagram link', icon: 'fa-brands:instagram', width: 24, height: 24, containerWidth: 21, containerHeight: 24 },
];

const visuallyHiddenStyle = {
  position: 'absolute' as const,
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  border: 0,
};

export default function USGSFooter() {
  return (
    <Box
      id="usgs-footer"
      data-testid="usgsFooter"
      component="footer"
      sx={{
        backgroundColor: USGS_BLUE,
        boxSizing: 'border-box',
        padding: '0.75rem 0',
        width: '100%',
        color: '#ffffff',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          maxWidth: '63rem',
          marginRight: 'auto',
          marginLeft: 'auto',
          padding: '0 1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '32px',
          overflow: 'visible',
          '@media (max-width: 639px)': {
            flexDirection: 'column',
            justifyContent: 'center',
            gap: '16px',
            py: '0.75rem'
          },
        }}
      >
        {/* .footer-doi — wrapper div is the flex child, not the ul */}
        <Box
          sx={{
            flex: '1 1 0',
            paddingLeft: 0,
            paddingRight: 0,
            '@media (min-width: 640px)': {
              paddingLeft: '32px',
            },
            '@media (min-width: 64rem)': {
              flex: '2 1 0',
              marginLeft: 'calc((63rem - 100vw) / 2)',
            },
          }}
        >
          <Box component="nav" aria-label="Footer links">
            <Box
              component="ul"
              sx={{
                marginTop: 0,
                marginBottom: 0,
                paddingLeft: 0,
                listStyle: 'none',
                lineHeight: '18px !important',
                '@media (max-width: 639px)': {
                  textAlign: 'center',
                },
              }}
            >
              {NAV_LINKS.map((item, index, arr) => (
                <Box
                  key={item.label}
                  component="li"
                  sx={{
                    display: 'inline-block',
                    lineHeight: '18px !important',
                    paddingRight: '2px',
                    paddingLeft: '5px',
                    '&::after': index < arr.length - 1 ? {
                      content: '"|"',
                      color: '#ffffff',
                      paddingLeft: '7px',
                      fontSize: '12px',
                    } : undefined,
                  }}
                >
                  <Link
                    href={item.href}
                    target="_blank"
                    underline="none"
                    sx={{
                      padding: '5px 0',
                      color: '#ffffff',
                      fontSize: '12px',
                      lineHeight: 1.15,
                      '&:hover': { textDecoration: 'underline', color: '#ffffff' },
                    }}
                  >
                    {item.label}
                  </Link>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* .footer-social-links */}
        <Box
          sx={{
            flex: '1 1 0',
            color: '#ffffff',
            fontSize: '12px',
            paddingRight: 0,
            minWidth: '200px',
            '@media (min-width: 640px)': {
              paddingRight: '32px',
            },
            '@media (max-width: 639px)': {
              minWidth: 'auto',
            },
          }}
        >
          <Box
            component="ul"
            sx={{
              float: 'right',
              marginTop: 0,
              marginBottom: 0,
              paddingLeft: 0,
              listStyle: 'none',
            }}
          >
            {SOCIAL_LINKS.map((item, index, arr) => (
              <Box
                key={item.label}
                component="li"
                sx={{
                  float: 'left',
                  height: item.containerHeight,
                  width: item.containerWidth,
                  margin: '0px 6px',
                  listStyle: 'none',
                  ...(index === arr.length - 1 && { marginRight: '0px' }),
                }}
              >
                <Link
                  href={item.href}
                  target="_blank"
                  aria-label={item.ariaLabel}
                  sx={{
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Icon icon={item.icon} width={item.width} height={item.width} color="white" />
                  <Box component="span" sx={visuallyHiddenStyle}>
                    {item.label}
                  </Box>
                </Link>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
